import { LANGS, langOf, roleLabel, t, type Account, type AuthUser } from '@procumi/shared';
import { useRef, useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { Avatar } from './Avatar';
import { Dialog } from './Dialog';
import { SecuritySection } from './SecuritySection';
import { SignatureEditor } from './SignatureEditor';

/** Latura pozei salvate: destul pentru ecranele cu densitate mare, mică pe disc. */
const SIDE = 256;

/**
 * Taie poza pătrat, din centru, și o micșorează la 256×256, în browser: pe
 * server ajunge o imagine de câteva zeci de KB, oricât de mare era cea de pe
 * telefon.
 */
async function squareJpeg(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const canvas = document.createElement('canvas');
  canvas.width = SIDE;
  canvas.height = SIDE;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, SIDE, SIDE);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error(t('Poza nu s-a putut citi.')))),
      'image/jpeg',
      0.86,
    ),
  );
}

/**
 * „Profilul meu”: poza de profil, pe care o văd colegii în echipă, comentarii și notificări.
 * La Achiziții, și comutatorul mailului de dimineață.
 */
export function ProfileDialog({
  account,
  toast,
  onChanged,
  onClose,
}: {
  account: Account;
  toast: Toast;
  onChanged: (patch: Partial<AuthUser>) => void;
  onClose: () => void;
}) {
  const user = account.user;
  const [busy, setBusy] = useState(false);
  const [morningBusy, setMorningBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);

  const setReport = async (on: boolean) => {
    setReportBusy(true);
    try {
      const r = await api.setReportMail(on);
      onChanged({ reportMail: r.reportMail });
      toast(r.reportMail ? t('Raportul lunar e pornit') : t('Raportul lunar e oprit'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setReportBusy(false);
    }
  };
  const input = useRef<HTMLInputElement>(null);
  const [langBusy, setLangBusy] = useState(false);

  // limba nouă: App vede schimbarea și reîncarcă pagina în noua limbă (lib/i18n.ts)
  const setLang = async (value: string) => {
    setLangBusy(true);
    try {
      const r = await api.setLocale(langOf(value));
      onChanged({ locale: r.locale });
    } catch (e) {
      toast(errMsg(e));
      setLangBusy(false);
    }
  };

  const setMorning = async (on: boolean) => {
    setMorningBusy(true);
    try {
      const r = await api.setMorningMail(on);
      onChanged({ morningMail: r.morningMail });
      toast(r.morningMail ? t('Mailul de dimineață e pornit') : t('Mailul de dimineață e oprit'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setMorningBusy(false);
    }
  };

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|heic|heif)$/.test(file.type) && !/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast(t('Alege o poză (JPG, PNG sau WebP).'));
      return;
    }
    setBusy(true);
    try {
      const r = await api.uploadAvatar(await squareJpeg(file));
      onChanged({ avatarAt: r.avatarAt });
      toast(t('Poza a fost salvată'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteAvatar();
      onChanged({ avatarAt: null });
      toast(t('Poza a fost ștearsă'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose}>
      <div className="dlg-h">
        <div>
          <h2>{t('Profilul meu')}</h2>
          <p className="hint">{t('Poza ta apare colegilor în echipă, la comentarii și în notificări.')}</p>
        </div>
      </div>
      <div className="dlg-b">
        <div className="profile-b">
          <Avatar name={user.name} size="xl" userId={user.id} avatarAt={user.avatarAt} />
          <div>
            <b className="profile-name">{user.name}</b>
            <span className="muted">
              {user.email} · {t(roleLabel(user.role))}
              {user.isAdmin ? t(' · administrator') : ''}
            </span>
            <div className="profile-acts">
              <button className="btn primary" disabled={busy} onClick={() => input.current?.click()}>
                {busy ? t('Se salvează…') : user.avatarAt ? t('Schimbă poza') : t('Pune o poză')}
              </button>
              {user.avatarAt ? (
                <button className="btn" disabled={busy} onClick={() => void remove()}>
                  {t('Șterge poza')}
                </button>
              ) : null}
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  void pick(f);
                }}
              />
            </div>
            <label className="field prof-lang">
              <span>{t('Limba')}</span>
              <select
                value={user.locale ?? 'ro'}
                disabled={langBusy}
                onChange={(e) => void setLang(e.target.value)}
              >
                {LANGS.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
              <span className="muted">
                {t('Aplicația și mailurile primite de tine. Furnizorii primesc mailurile în limba lor.')}
              </span>
            </label>
            {(user.morningEligible ?? user.role === 'buyer') ? (
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={user.morningMail !== false}
                  disabled={morningBusy}
                  onChange={(e) => void setMorning(e.target.checked)}
                />
                <span>
                  <b>{t('Mailul de dimineață')}</b>
                  <span className="muted">{t('Ce ai de rezolvat azi, de luni până vineri la 7:50.')}</span>
                </span>
              </label>
            ) : null}
            {user.role === 'approver' || user.isAdmin ? (
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={user.reportMail !== false}
                  disabled={reportBusy}
                  onChange={(e) => void setReport(e.target.checked)}
                />
                <span>
                  <b>{t('Raportul lunar pentru conducere')}</b>
                  <span className="muted">
                    {t('Pe 1 ale lunii: cât s-a comandat, cât s-a economisit și ce merită întrebat.')}
                  </span>
                </span>
              </label>
            ) : null}
          </div>
        </div>
        <SecuritySection toast={toast} />
        {account.user.role === 'site' ? null : (
          <SignatureEditor
            account={account}
            toast={toast}
            onSaved={(signature) => onChanged({ signature })}
          />
        )}
      </div>
      <div className="dlg-f">
        <button className="btn" onClick={onClose}>
          {t('Închide')}
        </button>
      </div>
    </Dialog>
  );
}
